<script setup lang="ts">
import { computed } from 'vue';

/**
 * Initials on a disc. Extracted from `AppLayout` when the org chart needed the same thing,
 * because two copies of "take the first letter of each word" is how two rules appear.
 */
const props = withDefaults(
  defineProps<{
    name: string;
    /** Diameter in `rem`-based Tailwind sizing, applied by the caller through `class`. */
    brand?: boolean;
    /** The person's photo. Initials are what shows when there is none, not a placeholder. */
    src?: string | null;
  }>(),
  { brand: false, src: null },
);

const initials = computed(() =>
  props.name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase(),
);

/*
 * The disc is decoration, not content: the name is always rendered beside it, so it is
 * `aria-hidden`. Announcing "PA" to a screen reader adds noise and no information.
 *
 * The comment lives here rather than in the template because a template comment makes the
 * component a two-node fragment, and attributes stop being inherited by the root element.
 */
</script>

<template>
  <img
    v-if="src"
    :src="src"
    alt=""
    aria-hidden="true"
    class="inline-block shrink-0 rounded-full object-cover"
  >
  <span
    v-else
    aria-hidden="true"
    class="inline-flex shrink-0 items-center justify-center rounded-full font-semibold tracking-wide text-[#f2f0ea]"
    :class="brand ? 'bg-brand-600' : 'bg-rail'"
  >{{ initials }}</span>
</template>
